import { motion, useAnimation } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

// Famous artworks as emoji representations
const ART_OBJECTS = [
  { emoji: "🖼️", label: "Mona Lisa" },
  { emoji: "🌻", label: "Sunflowers" },
  { emoji: "🌊", label: "Great Wave" },
  { emoji: "⭐", label: "Starry Night" },
  { emoji: "💋", label: "The Kiss" },
  { emoji: "🎭", label: "Theatre" },
  { emoji: "🎨", label: "Palette" },
  { emoji: "🏛️", label: "Museum" },
  { emoji: "🖌️", label: "Brush" },
  { emoji: "✨", label: "Sparkle" },
];

interface FallingObject {
  id: number;
  art: typeof ART_OBJECTS[0];
  startX: number;
  initialRotation: number;
  delay: number;
  size: number;
}

interface FallingArtObjectsProps {
  cardRef?: React.RefObject<HTMLDivElement>;
}

const FallingArtObjects = ({ cardRef }: FallingArtObjectsProps) => {
  const [cardRect, setCardRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const updatePositions = () => {
      if (cardRef?.current) {
        setCardRect(cardRef.current.getBoundingClientRect());
      }
    };

    const timer = setTimeout(updatePositions, 100);
    window.addEventListener("resize", updatePositions);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updatePositions);
    };
  }, [cardRef]);

  const fallingObjects = useMemo<FallingObject[]>(() => {
    if (!cardRect) return [];

    // Calculate card bounds as percentage of viewport so the emojis
    // rain down across the width of the confirmation card.
    const cardLeftPercent = (cardRect.left / window.innerWidth) * 100;
    const cardRightPercent = (cardRect.right / window.innerWidth) * 100;
    const cardWidthPercent = cardRightPercent - cardLeftPercent;

    return Array.from({ length: 18 }, (_, i) => ({
      id: i,
      art: ART_OBJECTS[Math.floor(Math.random() * ART_OBJECTS.length)],
      startX:
        cardLeftPercent +
        ((i % 6) / 6) * cardWidthPercent +
        Math.random() * (cardWidthPercent / 8),
      initialRotation: Math.random() * 20 - 10,
      delay: Math.floor(i / 6) * 0.8 + (i % 6) * 0.12,
      size: 32 + Math.random() * 12,
    }));
  }, [cardRect]);

  if (!cardRect) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-20">
      {fallingObjects.map((obj) => (
        <FallingArtPiece key={obj.id} object={obj} />
      ))}
    </div>
  );
};

interface FallingArtPieceProps {
  object: FallingObject;
}

const FallingArtPiece = ({ object }: FallingArtPieceProps) => {
  const controls = useAnimation();

  useEffect(() => {
    const xPosition = (object.startX / 100) * window.innerWidth;
    // Fall all the way through the viewport and exit off the bottom,
    // rather than piling up behind the navigation bar.
    const endY = window.innerHeight + object.size + 20;
    const fallDistance = endY + 80;
    const fallDuration = Math.max(1.6, Math.sqrt(fallDistance / 300));

    const animate = async () => {
      await controls.start({
        y: endY,
        opacity: 0,
        x: xPosition,
        rotate: object.initialRotation + (Math.random() - 0.5) * 90,
        transition: {
          delay: object.delay,
          duration: fallDuration,
          ease: [0.45, 0, 0.55, 1],
          opacity: {
            delay: object.delay + fallDuration - 0.5,
            duration: 0.5,
          },
        },
      });
    };

    animate();
  }, [controls, object]);

  return (
    <motion.div
      initial={{
        y: -80,
        x: (object.startX / 100) * window.innerWidth,
        rotate: object.initialRotation,
        opacity: 1,
        scale: 1,
      }}
      animate={controls}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        fontSize: object.size,
        lineHeight: 1,
        transformOrigin: "center bottom",
        filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.25))",
      }}
    >
      <span role="img" aria-label={object.art.label}>
        {object.art.emoji}
      </span>
    </motion.div>
  );
};

export default FallingArtObjects;
